import { type FC } from 'react'
import { useSalesforceBasedCalendarSync, useSalesforceBasedContactSync } from '../IntegrationSpecificSettings/SalesforceBased/hooks'
import { IntegrationServiceId } from '../../../../common/integrations'
import { type CrmIntegrationSettings, type CrmSettingsProviderProps } from './types'

interface SalesforceBasedProviderOptions {
  withCalendar: boolean
}

const makeSalesforceBasedProvider = (serviceId: IntegrationServiceId, { withCalendar }: SalesforceBasedProviderOptions): FC<CrmSettingsProviderProps> => {
  const ContactOnlyProvider: FC<CrmSettingsProviderProps> = ({ children }) => {
    const contact = useSalesforceBasedContactSync(serviceId)

    const settings: CrmIntegrationSettings = {
      contactSync: contact.autoSync ?? false,
      calendarSync: false,
      isSyncDisabled: contact.isSyncDisabled,
      isFetching: contact.isFetching,
      isSaving: contact.isSaving,
      save: async (patch) => {
        if (patch.contactSync !== undefined) {
          await contact.saveAutoSync(patch.contactSync)
        }
      },
    }

    return <>{children(settings)}</>
  }

  const WithCalendarProvider: FC<CrmSettingsProviderProps> = ({ children }) => {
    const contact = useSalesforceBasedContactSync(serviceId)
    const calendar = useSalesforceBasedCalendarSync(serviceId)

    const settings: CrmIntegrationSettings = {
      contactSync: contact.autoSync ?? false,
      calendarSync: calendar.enabled ?? false,
      isSyncDisabled: contact.isSyncDisabled,
      isFetching: contact.isFetching || calendar.isFetching,
      isSaving: contact.isSaving || calendar.isSaving,
      save: async (patch) => {
        const ops: Promise<boolean>[] = []
        if (patch.contactSync !== undefined) {
          ops.push(contact.saveAutoSync(patch.contactSync))
        }
        if (patch.calendarSync !== undefined) {
          ops.push(calendar.saveQuietly(patch.calendarSync))
        }
        await Promise.all(ops)
      },
    }

    return <>{children(settings)}</>
  }

  const Provider = withCalendar ? WithCalendarProvider : ContactOnlyProvider
  Provider.displayName = `${serviceId}CrmSettingsProvider`
  return Provider
}

export const SalesforceCrmSettingsProvider = makeSalesforceBasedProvider(IntegrationServiceId.SALESFORCE, { withCalendar: true })
export const PractifiCrmSettingsProvider = makeSalesforceBasedProvider(IntegrationServiceId.PRACTIFI, { withCalendar: false })
export const XLR8CrmSettingsProvider = makeSalesforceBasedProvider(IntegrationServiceId.XLR8, { withCalendar: false })
